import { Pencil, Trash2 } from 'lucide-react';
import type { Training } from '../../services/trainingLogApi';

interface Props {
    trainings: Training[];
    canEdit: boolean;
    onEdit: (training: Training) => void;
    onDelete: (training: Training) => void;
    onRowClick?: (training: Training) => void;
    showEmployee?: boolean;
}

function formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function TrainingList({ trainings, canEdit, onEdit, onDelete, onRowClick, showEmployee = false }: Props) {
    if (trainings.length === 0) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                No trainings found for the selected period.
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            {showEmployee && <th className="text-left px-4 py-3 font-medium text-gray-600">Employee</th>}
                            <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                            <th className="text-left px-4 py-3 font-medium text-gray-600">Course Topic</th>
                            <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                            <th className="text-center px-4 py-3 font-medium text-gray-600">Type</th>
                            <th className="text-right px-4 py-3 font-medium text-gray-600">Hours</th>
                            <th className="text-right px-4 py-3 font-medium text-gray-600">Cost EUR</th>
                            {canEdit && <th className="text-center px-4 py-3 font-medium text-gray-600 w-20">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {trainings.map((t) => (
                            <tr
                                key={t.Id}
                                className={`border-b border-gray-100 hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                                onClick={() => onRowClick?.(t)}
                            >
                                {showEmployee && (
                                    <td className="px-4 py-3 text-gray-900 font-medium">
                                        {t.EmployeeName || t.UserEmail}
                                    </td>
                                )}
                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                    {formatDate(t.StartDate)}
                                    {t.StartDate !== t.EndDate && ` - ${formatDate(t.EndDate)}`}
                                </td>
                                <td className="px-4 py-3 text-gray-900 max-w-xs truncate" title={t.CourseTopic}>
                                    {t.CourseTopic}
                                    {t.TrainingOrganizer && (
                                        <span className="text-gray-400 text-xs ml-2">({t.TrainingOrganizer})</span>
                                    )}
                                </td>
                                <td className="px-4 py-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                        {t.Category}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        t.InternalExternal === 'Internal'
                                            ? 'bg-green-50 text-green-700'
                                            : 'bg-orange-50 text-orange-700'
                                    }`}>
                                        {t.InternalExternal}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900 font-medium">
                                    {t.DurationHours}h
                                </td>
                                <td className="px-4 py-3 text-right text-gray-600">
                                    {t.CostsEur != null && t.CostsEur > 0
                                        ? `€ ${t.CostsEur.toLocaleString('de-CH', { minimumFractionDigits: 0 })}`
                                        : '-'}
                                </td>
                                {canEdit && (
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onEdit(t); }}
                                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                title="Edit"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); onDelete(t); }}
                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
